import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Pagination,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ReplayIcon from '@mui/icons-material/Replay';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import axiosInstance from '../../../services/axiosInterceptors';
import nipponBotApiEndpoints from '../../../services/nipponBotApiEndpoints';
import BranchCandidateForm from './BranchCandidateForm';
import { theme as colorTheme } from '../../../Convoze.theme';

const primary = colorTheme('palette', 'primary').value;
const PRIMARY_HOVER = '#5f2ab8';

const primaryContainedButtonSx = {
  textTransform: 'none',
  bgcolor: primary,
  borderRadius: '12px',
  '&:hover': { bgcolor: PRIMARY_HOVER }
};

const primaryOutlinedButtonSx = {
  textTransform: 'none',
  color: primary,
  borderColor: primary,
  '&:hover': {
    borderColor: primary,
    bgcolor: `${primary}14`
  }
};

const tableHeadingSx = {
  textTransform: 'uppercase',
  fontWeight: 550,
  color: '#6b7280',
  fontSize: '0.7rem',
  backgroundColor: '#f9fafb',
  whiteSpace: 'nowrap'
};

const paginationSx = {
  '& .MuiPaginationItem-root': { color: primary, borderColor: primary },
  '& .MuiPaginationItem-root.Mui-selected': { backgroundColor: `${primary} !important`, color: '#fff !important' },
  '& .MuiPaginationItem-root:hover': { backgroundColor: '#E0E7FF' }
};

const RESUME_STATUS_STYLES = {
  completed: { color: '#1e7e34', bg: '#e6f4ea' },
  partial: { color: '#b45309', bg: '#fef3e2' },
  failed: { color: '#c62828', bg: '#fdecea' },
  processing: { color: '#0277bd', bg: '#e3f2fd' }
};

const INTERVIEW_STATUS_STYLES = {
  completed: { color: '#1e7e34', bg: '#e6f4ea' },
  scheduled: { color: '#0277bd', bg: '#e3f2fd' },
  dispatched: { color: '#7c4d00', bg: '#fff4d6' },
  rescheduled: { color: '#8a5a00', bg: '#fff7e0' },
  failed: { color: '#c62828', bg: '#fdecea' },
  not_done: { color: '#616161', bg: '#f0f0f0' }
};

const DEFAULT_STATUS_STYLE = { color: '#616161', bg: '#f0f0f0' };

const BRANCH_CANDIDATE_PAGE_LIMIT = 10;

const getCandidateRowsFromListResponse = (response) => {
  const payload = response?.data?.data;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload)) return payload;
  return [];
};

const getPaginatorFromListResponse = (response) => {
  const payload = response?.data?.data;
  return payload?.paginator || response?.data?.paginator || {};
};

const normalizePaginator = ({ paginator = {}, rowCount = 0, requestedPage = 1, limit = BRANCH_CANDIDATE_PAGE_LIMIT }) => {
  const itemCount = Number(paginator.itemCount ?? paginator.totalDocs ?? rowCount);
  const perPage = Number(paginator.perPage ?? paginator.limit ?? limit);
  const currentPage = Number(paginator.currentPage ?? paginator.page ?? requestedPage);
  const pageCount = Number(paginator.pageCount ?? paginator.totalPages ?? Math.max(1, Math.ceil(itemCount / Math.max(perPage, 1))));

  return {
    ...paginator,
    itemCount,
    perPage,
    currentPage,
    pageCount
  };
};

const formatResumeStatusLabel = (status) => (status ? 'Uploaded' : 'Not uploaded');

const formatInterviewStatusLabel = (status = '') => {
  const labels = {
    completed: 'Completed',
    scheduled: 'Scheduled',
    dispatched: 'Dispatched',
    rescheduled: 'Rescheduled',
    failed: 'Failed',
    not_done: 'Not Done'
  };
  return labels[status] || 'Not Done';
};

const formatFriendlyInterviewReason = (reason = '') => {
  const normalized = String(reason || '').toLowerCase();
  if (!normalized) return '';
  if (normalized.includes('no sip messages') || normalized.includes('sip data') || normalized.includes('awaiting sip')) {
    return 'Call was not connected.';
  }
  if (normalized.includes('not picked') || normalized.includes('no answer')) {
    return 'Candidate did not pick up.';
  }
  if (normalized.includes('disconnect') || normalized.includes('hangup')) {
    return 'Call got disconnected.';
  }
  if (normalized.includes('less than 5 seconds')) {
    return 'Call ended too quickly.';
  }
  return reason;
};

const formatRetryDateTime = (value) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleString('en-IN', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true
  });
};

const getInterviewStatusDetail = (candidate = {}) => {
  const retryDateTime = formatRetryDateTime(candidate.nextRetryAt);
  if (retryDateTime && Number(candidate.interviewAttemptCount || 0) < Number(candidate.interviewMaxAttempts || 3)) {
    return `Call failed. Reattempting on ${retryDateTime}.`;
  }

  const attemptCount = Number(candidate.interviewAttemptCount || 0);
  const maxAttempts = Number(candidate.interviewMaxAttempts || 3);
  const status = String(candidate.interviewStatus || '');
  const shouldShowFailureReason = ['failed', 'rescheduled'].includes(status);
  const shouldShowProgressReason = ['dispatched'].includes(status);
  const reason = formatFriendlyInterviewReason(
    candidate.interviewStatusReason || (shouldShowFailureReason ? candidate.interviewFailureReason : '') || ''
  );
  if (reason) return reason;
  if (shouldShowProgressReason && attemptCount) return `Call attempt ${attemptCount} of ${maxAttempts} is in progress.`;
  return '';
};

const formatDateTimeParts = (value) => {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return { date: date.toLocaleDateString(), time: date.toLocaleTimeString() };
};

const DateTimeCell = ({ value }) => {
  const parts = formatDateTimeParts(value);
  if (!parts) return <TableCell sx={{ whiteSpace: 'nowrap' }}>-</TableCell>;
  return (
    <TableCell sx={{ whiteSpace: 'nowrap' }}>
      <Box>{parts.date}</Box>
      <Box sx={{ color: 'text.secondary', fontSize: '0.75rem' }}>{parts.time}</Box>
    </TableCell>
  );
};

export default function BranchCandidatesTab({ onMessage }) {
  const token = localStorage.getItem('token');

  const [candidates, setCandidates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [page, setPage] = useState(1);
  const [paginationData, setPaginationData] = useState({});

  const [agents, setAgents] = useState([]);
  const [retryingCandidateId, setRetryingCandidateId] = useState('');

  const [showForm, setShowForm] = useState(false);
  const [showScheduleForm, setShowScheduleForm] = useState(false);

  useEffect(() => {
    const loadAgents = async () => {
      try {
        const response = await axiosInstance.get(nipponBotApiEndpoints.branchManagerInterviewAgents, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setAgents((response?.data?.data || []).filter((agent) => agent.isActive));
      } catch (error) {
        onMessage?.(error?.response?.data?.message || 'Failed to load interview agents.', 'error');
      }
    };
    loadAgents();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(searchQuery);
      setPage(1);
    }, 500);
    return () => clearTimeout(handler);
  }, [searchQuery]);

  const loadCandidates = async ({ silent = false } = {}) => {
    if (!silent) setLoading(true);
    try {
      const response = await axiosInstance.get(nipponBotApiEndpoints.branchManagerCandidateList, {
        headers: { Authorization: `Bearer ${token}` },
        params: { page, limit: BRANCH_CANDIDATE_PAGE_LIMIT, searchTerm: debouncedSearch }
      });
      const candidateRows = getCandidateRowsFromListResponse(response);
      setCandidates(candidateRows);
      setPaginationData(normalizePaginator({
        paginator: getPaginatorFromListResponse(response),
        rowCount: candidateRows.length,
        requestedPage: page
      }));
    } catch (error) {
      onMessage?.(error?.response?.data?.message || 'Failed to load candidate profiles.', 'error');
    } finally {
      if (!silent) setLoading(false);
    }
  };

  useEffect(() => {
    loadCandidates();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, debouncedSearch]);

  useEffect(() => {
    const interval = setInterval(() => {
      loadCandidates({ silent: true });
    }, 10000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, debouncedSearch]);

  const handleSaveCandidate = async () => {
    await loadCandidates();
    onMessage?.('Candidate uploaded and interview scheduled successfully.', 'success');
  };

  const handleRetryInterview = async (candidate) => {
    if (!candidate?.interviewScheduleId) {
      onMessage?.('No failed interview schedule is available for retry.', 'error');
      return;
    }

    setRetryingCandidateId(candidate.id);
    try {
      await axiosInstance.post(`${nipponBotApiEndpoints.branchManagerInterviewRetryFailed}/${candidate.interviewScheduleId}/retry-failed`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });

      await loadCandidates();
      onMessage?.('Calls have been queued for another try.', 'success');
    } catch (error) {
      onMessage?.(error?.response?.data?.message || 'Failed to retry interview.', 'error');
    } finally {
      setRetryingCandidateId('');
    }
  };

  return (
    <Box sx={{ display: 'grid', gap: 2, minWidth: 0 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center">
        <TextField
          size="small"
          label="Search Candidates"
          placeholder="Search by name, email, or SAP Code"
          value={searchQuery}
          onChange={(event) => setSearchQuery(event.target.value)}
          sx={{ width: 280 }}
        />
        <Stack direction="row" spacing={1}>
          <Button
            variant="outlined"
            startIcon={<EventAvailableIcon />}
            onClick={() => setShowScheduleForm(true)}
            sx={primaryOutlinedButtonSx}
          >
            Schedule Interview
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setShowForm(true)}
            sx={primaryContainedButtonSx}
          >
            Add Candidate
          </Button>
        </Stack>
      </Box>

      {loading ? (
        <Box sx={{ width: '100%', height: 480, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <CircularProgress />
        </Box>
      ) : !candidates.length ? (
        <Box sx={{ width: '100%', minHeight: 480, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Alert severity="info">No candidates available yet.</Alert>
        </Box>
      ) : (
        <Paper sx={{ boxShadow: 0, border: 1, borderColor: 'grey.200', borderRadius: 2, overflow: 'hidden' }}>
          <TableContainer sx={{ maxHeight: 560, overflowX: 'auto', overflowY: 'auto', width: '100%' }}>
            <Table size="small" stickyHeader sx={{ minWidth: 800 }}>
              <TableHead>
                <TableRow>
                  <TableCell sx={tableHeadingSx}>Created On</TableCell>
                  <TableCell sx={tableHeadingSx}>Hiring Manager SAP Code</TableCell>
                  <TableCell sx={tableHeadingSx}>Candidate Name</TableCell>
                  <TableCell sx={tableHeadingSx}>Language</TableCell>
                  <TableCell sx={tableHeadingSx}>Latest Interview</TableCell>
                  <TableCell sx={tableHeadingSx}>Resume Status</TableCell>
                  <TableCell sx={tableHeadingSx}>Interview Status</TableCell>
                  <TableCell sx={tableHeadingSx}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {candidates.map((candidate) => (
                  <TableRow key={candidate.id} hover>
                    <DateTimeCell value={candidate.createdAt} />
                    <TableCell>{candidate.sapCode || '-'}</TableCell>
                    <TableCell>
                      <Box>{candidate.fullName || 'Unnamed Candidate'}</Box>
                    </TableCell>
                    <TableCell>{candidate.interviewAgentName || '-'}</TableCell>
                    <DateTimeCell value={candidate.lastCallScheduledAt} />
                    <TableCell>
                      <Chip
                        size="small"
                        label={formatResumeStatusLabel(candidate.resume?.id)}
                        variant="outlined"
                        sx={{
                          fontWeight: 600,
                          borderWidth: 1.5,
                          borderColor: (candidate.resume?.id ? RESUME_STATUS_STYLES.completed : DEFAULT_STATUS_STYLE).color,
                          color: (candidate.resume?.id ? RESUME_STATUS_STYLES.completed : DEFAULT_STATUS_STYLE).color,
                          backgroundColor: (candidate.resume?.id ? RESUME_STATUS_STYLES.completed : DEFAULT_STATUS_STYLE).bg
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <Stack spacing={0.5} alignItems="flex-start">
                        <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap">
                          <Tooltip title={getInterviewStatusDetail(candidate)} disableHoverListener={!getInterviewStatusDetail(candidate)}>
                            <Chip
                              size="small"
                              label={formatInterviewStatusLabel(candidate.interviewStatus)}
                              variant="outlined"
                              sx={{
                                fontWeight: 600,
                                borderWidth: 1.5,
                                borderColor: (INTERVIEW_STATUS_STYLES[candidate.interviewStatus] || DEFAULT_STATUS_STYLE).color,
                                color: (INTERVIEW_STATUS_STYLES[candidate.interviewStatus] || DEFAULT_STATUS_STYLE).color,
                                backgroundColor: (INTERVIEW_STATUS_STYLES[candidate.interviewStatus] || DEFAULT_STATUS_STYLE).bg
                              }}
                            />
                          </Tooltip>
                          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>
                            Attempts: {candidate.interviewAttemptCount || 0}/{candidate.interviewMaxAttempts || 3}
                          </Typography>
                        </Stack>
                        {getInterviewStatusDetail(candidate) && (
                          <Typography variant="caption" color="text.secondary" sx={{ maxWidth: 260, display: 'block' }}>
                            {getInterviewStatusDetail(candidate)}
                          </Typography>
                        )}
                      </Stack>
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={0.5} alignItems="center" flexWrap="wrap">
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={retryingCandidateId === candidate.id ? <CircularProgress size={14} /> : <ReplayIcon fontSize="small" />}
                          disabled={retryingCandidateId === candidate.id || candidate.interviewStatus !== 'failed' || !candidate.interviewScheduleId}
                          onClick={() => handleRetryInterview(candidate)}
                          sx={{ ...primaryOutlinedButtonSx, minWidth: 108 }}
                        >
                          {retryingCandidateId === candidate.id ? 'Retrying...' : 'Retry Failed'}
                        </Button>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          <Stack direction="row" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={1} sx={{ p: 2 }}>
            <Typography variant="body2" color="text.secondary">
              {candidates.length > 0
                ? `Showing ${((paginationData?.currentPage || 1) - 1) * (paginationData?.perPage || 10) + 1}-${Math.min((paginationData?.currentPage || 1) * (paginationData?.perPage || 10), paginationData?.itemCount || candidates.length)} of ${paginationData?.itemCount || candidates.length} results`
                : 'No results found'}
            </Typography>
            <Pagination
              count={paginationData?.pageCount || 1}
              page={paginationData?.currentPage || page}
              onChange={(event, value) => setPage(value)}
              sx={paginationSx}
            />
          </Stack>
        </Paper>
      )}

      <BranchCandidateForm
        open={showForm}
        onClose={() => setShowForm(false)}
        onSave={handleSaveCandidate}
        onMessage={onMessage}
        agents={agents}
        token={token}
      />

      <BranchCandidateForm
        open={showScheduleForm}
        onClose={() => setShowScheduleForm(false)}
        onSave={handleSaveCandidate}
        onMessage={onMessage}
        agents={agents}
        token={token}
        mode="schedule"
        candidates={candidates}
      />
    </Box>
  );
}
